import React, { useRef, useState, useEffect } from "react";

// prime react component
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dialog } from "primereact/dialog";

// formik component
import { Formik, Form, ErrorMessage } from "formik";

// master tab
import { MasterTabs } from "../MasterTabs";

// datatable component
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../../appconfig/Settings";

// crop table column field
import { CropColumns } from "../../../appconfig/DataTableColumns";

//  api services
import {
  getAllDetails,
  postAllData,
  updateData,
} from "../../../service/ProductService";

// api slug from apiconstant
import {
  ADD_CROP,
  GET_CROP_LIST,
  UPDATE_CROP,
} from "../../../config/ApiConstant";

// crop validation schema
import { CropValidationSchema } from "../../../config/Validate";
import { globalNameFilterTemplate } from "../../../appconfig/AppHelper";

// type define for field
import { CropType } from "../../../appconfig/interfaces";
import { globalFieldTemplate } from "../../../appconfig/TemplateSetting";
import { InputNumber } from "primereact/inputnumber";

const Crops: React.FC = () => {
  document.title = "Crops | Agriown";

  //  ref hook
  const formikRef = useRef<any>(null);

  // Set Toast
  const toast = useRef<Toast>(null);

  // state for Filter
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  // state for dialog
  const [addCropDialog, setAddCropDialog] = useState<boolean>(false);

  // state for loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // state for store crop list
  const [cropList, setCropList] = useState<CropType[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedCrop, setSelectedCrop] = useState<any>([]);

  // state for slot selection
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    crop_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // initial values define for crop field validation based on edit mode
  const initialFormValues: CropType = {
    crop_name: editMode ? selectedCrop?.crop_name ?? "" : "",
    crop_name_hi: editMode ? selectedCrop?.crop_name_hi ?? "" : "",
    crop_name_gu: editMode ? selectedCrop?.crop_name_gu ?? "" : "",

    // n_from: editMode ? selectedCrop?.n_from ?? null : null,
    // n_to: editMode ? selectedCrop?.n_to ?? null : null,

    // p_from: editMode ? selectedCrop?.p_from ?? null : null,
    // p_to: editMode ? selectedCrop?.p_to ?? null : null,

    // k_from: editMode ? selectedCrop?.k_from ?? null : null,
    // k_to: editMode ? selectedCrop?.k_to ?? null : null,

    // ph_from: editMode ? selectedCrop?.ph_from ?? null : null,
    // ph_to: editMode ? selectedCrop?.ph_to ?? null : null,

    // ec: editMode ? selectedCrop?.ec ?? null : null,
    // oc: editMode ? selectedCrop?.oc ?? null : null,
  };

  // Fetch crop list on page load
  useEffect(() => {
    getCropList();
  }, []);

  // crop list api call
  const getCropList = async () => {
    try {
      const response = await getAllDetails(GET_CROP_LIST);

      if (response) {
        setCropList(response);
        setPageLoad(true);
      } else {
        setCropList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  //  add & edit crop function api call
  const handleCropSubmit = async (values: CropType, { resetForm }: any) => {
    setLoading(true);
    try {
      const payload: any = {
        crop_name: values.crop_name,
        // n_from: values.n_from,
        // n_to: values.n_to,
        // p_from: values.p_from,
        // p_to: values.p_to,
        // k_from: values.k_from,
        // k_to: values.k_to,
        // ph_from: values.ph_from,
        // ph_to: values.ph_to,
        // ec: values.ec,
        // oc: values.oc,
      };

      if (values.crop_name_hi && values.crop_name_hi.trim() !== "") {
        payload.crop_name_hi = values.crop_name_hi;
      }
      if (values.crop_name_gu && values.crop_name_gu.trim() !== "") {
        payload.crop_name_gu = values.crop_name_gu;
      }

      const response =
        editMode && selectedCrop.id !== null
          ? await updateData(UPDATE_CROP, selectedCrop.id, payload)
          : await postAllData(ADD_CROP, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setAddCropDialog(false);
        setEditMode(false);
        getCropList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit button handler
  const edit = (rowData: CropType) => {
    setEditMode(true);
    setSelectedCrop(rowData);
    setAddCropDialog(true);
  };

  // Action column with edit icon
  const actionBodyTemplate = (rowData: CropType) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        className="mr-2 p-button-secondary"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() => edit(rowData)}
      />
    </div>
  );

  // Toolbar - Left
  const leftToolbarTemplate = () => (
    <>
      <span className="mr-0 md:mr-3 search-bar">
        <i className="pi pi-search" />
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    </>
  );

  // Clear Filters
  const clear = () => {
    setGlobalFilter("");
    setFilters({ ...emptyFilters });
  };

  // Toolbar - Right
  const rightToolbarTemplate = () => (
    <>
      <Button
        icon="pi pi-filter-slash"
        label="Clear"
        onClick={() => clear()}
        className="md:mr-2 p-button-outlined p-button-primary"
      />
      <Button
        label="Add Crop"
        icon="pi pi-plus"
        className="ml-2 p-button toolbar-button"
        onClick={() => {
          setEditMode(false);
          setAddCropDialog(true);
        }}
      />
    </>
  );

  // Hide dialog and reset state
  const hideAddCropDialog = () => {
    setAddCropDialog(false);
    setEditMode(false);
  };

  // N_FromTo_ToTemplate
  // const N_FromTo_ToTemplate = (rowData: any) => {
  //   return (
  //     <>
  //       <div>{!window.cn(rowData?.n_from) ? rowData?.n_from : ""} - {!window.cn(rowData?.n_to) ? rowData?.n_to : ''}</div>
  //     </>
  //   )
  // }

  // P_FromTo_ToTemplate
  // const P_FromTo_ToTemplate = (rowData: any) => {
  //   return (
  //     <>
  //       <div>{!window.cn(rowData?.p_from) ? rowData?.p_from : ""} - {!window.cn(rowData?.p_to) ? rowData?.p_to : ''}</div>
  //     </>
  //   )
  // }

  // K_FromTo_ToTemplate
  // const K_FromTo_ToTemplate = (rowData: any) => {
  //   return (
  //     <>
  //       <div>{!window.cn(rowData?.k_from) ? rowData?.k_from : ""} - {!window.cn(rowData?.k_to) ? rowData?.k_to : ''}</div>
  //     </>
  //   )
  // }

  // P_FromTo_ToTemplate
  // const PH_FromTo_ToTemplate = (rowData: any) => {
  //   return (
  //     <>
  //       <div>{!window.cn(rowData?.ph_from) ? rowData?.ph_from : ""} - {!window.cn(rowData?.ph_to) ? rowData?.ph_to : ''}</div>
  //     </>
  //   )
  // }

  // model footer button cancel & submit
  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={hideAddCropDialog}
        className="p-button-secondary"
      />
      <Button
        label={editMode ? "Update" : "Save"}
        icon="pi pi-check"
        type="button"
        loading={loading}
        disabled={loading}
        onClick={() => formikRef.current?.handleSubmit()}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <MasterTabs index={1} />
            <Toast ref={toast} />

            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

            {pageLoad ? (
              <DataTable
                value={cropList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                globalFilterFields={['crop_name']}
                rows={defaultRowOptions}
                paginator={cropList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                onFilter={(e: any) => setFilters(e.filters)}
                emptyMessage="No Data Found"
                removableSort
              >
                {CropColumns.map((col) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  } else if (col.field === "crop_name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        sortable
                        filter
                        body={globalFieldTemplate}
                        filterElement={globalNameFilterTemplate}
                        header={col.header}
                      />
                    );
                  } 
                  // else if (col.field === "n_from_to_to") {
                  //   return (
                  //     <Column
                  //       key={col.field}
                  //       field={col.field}
                  //       body={N_FromTo_ToTemplate}
                  //       header={col.header}
                  //     />
                  //   );
                  // }
                  //  else if (col.field === "p_from_to_to") {
                  //   return (
                  //     <Column
                  //       key={col.field}
                  //       field={col.field}
                  //       body={P_FromTo_ToTemplate}
                  //       header={col.header}
                  //     />
                  //   );
                  // }
                  //  else if (col.field === "k_from_to_to") {
                  //   return (
                  //     <Column
                  //       key={col.field}
                  //       field={col.field}
                  //       body={K_FromTo_ToTemplate}
                  //       header={col.header}
                  //     />
                  //   );
                  // }
                  //  else if (col.field === "ph_from_to_to") {
                  //   return (
                  //     <Column
                  //       key={col.field}
                  //       field={col.field}
                  //       body={PH_FromTo_ToTemplate}
                  //       header={col.header}
                  //     />
                  //   );
                  // }
                   else {
                    return (
                      <Column
                        key={col.field}
                        sortable
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {CropColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Crop Dialog with Formik */}
      <Dialog
        visible={addCropDialog}
        style={{ width: "600px" }}
        header={editMode ? "Edit Crop" : "Add Crop"}
        modal
        className="mx-3 lg:mx-0"
        draggable={false}
        onHide={hideAddCropDialog}
        footer={footerContent}
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={CropValidationSchema}
          enableReinitialize
          innerRef={formikRef}
          onSubmit={handleCropSubmit}
        >
          {({
            handleSubmit,
            values,
            handleBlur,
            handleChange,
            // setFieldValue,
            // setFieldTouched,
            touched,
            errors,
          }) => (
            <Form className="grid p-fluid" onSubmit={handleSubmit}>
              <div className="field col-4">
                <label htmlFor="crop_name">Name (English) *</label>
                <InputText
                  id="crop_name"
                  name="crop_name"
                  value={values.crop_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Name (English)"
                  className={
                    touched.crop_name && errors.crop_name ? "p-invalid" : ""
                  }
                />
                <small className="p-error">
                  <ErrorMessage name="crop_name" />
                </small>
              </div>

              {/* Hindi Crop Name */}
              <div className="field col-4">
                <label htmlFor="crop_name_hi">Name (Hindi)</label>
                <InputText
                  id="crop_name_hi"
                  name="crop_name_hi"
                  value={values.crop_name_hi}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Name (Hindi)"
                />
              </div>

              {/* Gujarati Crop Name */}
              <div className="field col-4">
                <label htmlFor="crop_name_gu">Name (Gujarati)</label>
                <InputText
                  id="crop_name_gu"
                  name="crop_name_gu"
                  value={values.crop_name_gu}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Name (Gujarati)"
                />
              </div>

              {/* <div className="field col-12">
                <label>N *</label>

                <div className="grid">
                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">n_from</span>
                      <InputNumber
                        value={values.n_from}
                        onValueChange={(e) => setFieldValue("n_from", e.value)}
                        onBlur={() => setFieldTouched("n_from", true)}
                        className={touched.n_from && errors.n_from ? "p-invalid" : ""}
                      />
                    </div>
                    {touched.n_from && errors.n_from && (
                      <small className="p-error block mt-1">{errors.n_from}</small>
                    )}
                  </div>

                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">n_to</span>
                      <InputNumber
                        value={values.n_to}
                        onValueChange={(e) => setFieldValue("n_to", e.value)}
                        onBlur={() => setFieldTouched("n_to", true)}
                        className={touched.n_to && errors.n_to ? "p-invalid" : ""}
                      />
                    </div>
                    {touched.n_to && errors.n_to && (
                      <small className="p-error block mt-1">{errors.n_to}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="field col-12">
                <label>P *</label>

                <div className="grid">
                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">p_from</span>
                      <InputNumber
                        value={values.p_from}
                        onValueChange={(e) => setFieldValue("p_from", e.value)}
                        onBlur={() => setFieldTouched("p_from", true)}
                        className={touched.p_from && errors.p_from ? "p-invalid" : ""}
                      />
                    </div>

                    {touched.p_from && errors.p_from && (
                      <small className="p-error block mt-1">{errors.p_from}</small>
                    )}
                  </div>

                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">p_to</span>
                      <InputNumber
                        value={values.p_to}
                        onValueChange={(e) => setFieldValue("p_to", e.value)}
                        onBlur={() => setFieldTouched("p_to", true)}
                        className={touched.p_to && errors.p_to ? "p-invalid" : ""}
                      />
                    </div>

                    {touched.p_to && errors.p_to && (
                      <small className="p-error block mt-1">{errors.p_to}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="field col-12">
                <label>K *</label>

                <div className="grid">
                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">k_from</span>
                      <InputNumber
                        value={values.k_from}
                        onValueChange={(e) => setFieldValue("k_from", e.value)}
                        onBlur={() => setFieldTouched("k_from", true)}
                        className={touched.k_from && errors.k_from ? "p-invalid" : ""}
                      />
                    </div>

                    {touched.k_from && errors.k_from && (
                      <small className="p-error block mt-1">{errors.k_from}</small>
                    )}
                  </div>

                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">k_to</span>
                      <InputNumber
                        value={values.k_to}
                        onValueChange={(e) => setFieldValue("k_to", e.value)}
                        onBlur={() => setFieldTouched("k_to", true)}
                        className={touched.k_to && errors.k_to ? "p-invalid" : ""}
                      />
                    </div>

                    {touched.k_to && errors.k_to && (
                      <small className="p-error block mt-1">{errors.k_to}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="field col-12">
                <label>PH *</label>

                <div className="grid">
                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">ph_from</span>
                      <InputNumber
                        value={values.ph_from}
                        onValueChange={(e) => setFieldValue("ph_from", e.value)}
                        onBlur={() => setFieldTouched("ph_from", true)}
                        className={touched.ph_from && errors.ph_from ? "p-invalid" : ""}
                      />
                    </div>

                    {touched.ph_from && errors.ph_from && (
                      <small className="p-error block mt-1">{errors.ph_from}</small>
                    )}
                  </div>

                  <div className="col-6">
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">ph_to</span>
                      <InputNumber
                        value={values.ph_to}
                        onValueChange={(e) => setFieldValue("ph_to", e.value)}
                        onBlur={() => setFieldTouched("ph_to", true)}
                        className={touched.ph_to && errors.ph_to ? "p-invalid" : ""}
                      />
                    </div>

                    {touched.ph_to && errors.ph_to && (
                      <small className="p-error block mt-1">{errors.ph_to}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="field col-6">
                <label htmlFor="ec">EC *</label>

                <InputNumber
                  id="ec"
                  value={values.ec}
                  onValueChange={(e) => setFieldValue("ec", e.value)}
                  onBlur={() => setFieldTouched("ec", true)}
                  className={touched.ec && errors.ec ? "p-invalid" : ""}
                />

                {touched.ec && errors.ec && (
                  <small className="p-error block mt-1">{errors.ec}</small>
                )}
              </div>

              <div className="field col-6">
                <label htmlFor="oc">OC *</label>

                <InputNumber
                  id="oc"
                  value={values.oc}
                  onValueChange={(e) => setFieldValue("oc", e.value)}
                  onBlur={() => setFieldTouched("oc", true)}
                  className={touched.oc && errors.oc ? "p-invalid" : ""}
                />

                {touched.oc && errors.oc && (
                  <small className="p-error block mt-1">{errors.oc}</small>
                )}
              </div> */}
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default Crops;
